import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const LOGO_URL = "https://public.readdy.ai/ai/img_res/f7c36657-f3e9-41c7-b237-9b499e377f01.png";

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "zh", label: "中" },
  { code: "ja", label: "日" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language.slice(0, 2) || "en");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = () => { setLangOpen(false); setMenuOpen(false); };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleLangChange = (code: string) => {
    setCurrentLang(code);
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const navLinks = [
    { label: t("tabifoto.nav.home"), path: "/" },
    { label: t("tabifoto.nav.booking"), path: "/booking" },
    { label: t("tabifoto.nav.philosophy"), path: "/philosophy" },
  ];

  const isTransparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isTransparent
          ? "bg-transparent"
          : "bg-[#F7F6F3]/90 backdrop-blur-md border-b border-[#D9D9D9]/60"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src={LOGO_URL}
            alt="TaBiFoTo"
            className="h-8 lg:h-10 w-auto object-contain"
          />
          <span
            className={`hidden sm:block text-base lg:text-lg tracking-[0.18em] font-light transition-colors duration-500 font-['Cormorant_Garamond',serif] ${
              isTransparent ? "text-white/90" : "text-[#2C2C2C]"
            }`}
            style={{ letterSpacing: "0.18em" }}
          >
            TaBiFoTo
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm tracking-widest uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? "text-white/80 hover:text-white"
                  : "text-[#2C2C2C]/70 hover:text-[#2C2C2C]"
              } ${location.pathname === link.path ? (isTransparent ? "text-white" : "text-[#2C2C2C]") : ""}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/photographer/apply"
            className={`text-xs tracking-widest uppercase px-4 py-1.5 rounded-full border transition-all duration-200 whitespace-nowrap cursor-pointer ${
              isTransparent
                ? "border-white/30 text-white/60 hover:border-white/60 hover:text-white/90"
                : "border-[#C8B8A6]/60 text-[#C8B8A6] hover:border-[#C8B8A6] hover:text-[#2C2C2C]"
            }`}
          >
            {t("tabifoto.nav.apply")}
          </Link>
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setLangOpen(!langOpen); setMenuOpen(false); }}
              className={`flex items-center gap-1 text-xs tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? "border-white/40 text-white/80 hover:border-white/80 hover:text-white"
                  : "border-[#D9D9D9] text-[#2C2C2C]/70 hover:border-[#C8B8A6] hover:text-[#2C2C2C]"
              }`}
            >
              {currentLang.toUpperCase()}
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-xs" />
              </div>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-[#F7F6F3] border border-[#D9D9D9] rounded-lg shadow-sm overflow-hidden min-w-[80px]">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangChange(lang.code)}
                    className={`w-full text-center text-xs py-2 px-4 tracking-widest cursor-pointer transition-colors duration-150 whitespace-nowrap ${
                      currentLang === lang.code
                        ? "bg-[#C8B8A6]/30 text-[#2C2C2C]"
                        : "text-[#2C2C2C]/60 hover:bg-[#D9D9D9]/30 hover:text-[#2C2C2C]"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
              isTransparent ? "text-white" : "text-[#2C2C2C]"
            }`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); setLangOpen(false); }}
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {menuOpen && (
        <div className="lg:hidden bg-[#F7F6F3] border-t border-[#D9D9D9]/60 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className="text-sm tracking-widest uppercase text-[#2C2C2C]/70 hover:text-[#2C2C2C] transition-colors py-2 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/photographer/apply"
            onClick={() => setMenuOpen(false)}
            className="text-sm tracking-widest uppercase text-[#C8B8A6] hover:text-[#2C2C2C] transition-colors py-2 cursor-pointer whitespace-nowrap"
          >
            {t("tabifoto.nav.apply")}
          </Link>
        </div>
      )}
    </header>
  );
}
